import React, { useEffect, useState } from 'react';
import { Autocomplete,Switch } from '@mui/material';
import { useTheme } from '@emotion/react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Button,
    Grid,
    Popper,
    TextField,
    FormControlLabel,
    FormControl,
    FormLabel,
    RadioGroup,
    Radio,
    MenuItem,
    InputLabel,
    Select,
    Typography,
    CircularProgress,
    Skeleton,
    Checkbox,
    Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import toast from 'react-hot-toast';
import countries from 'utils/countries_states.json';
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import parsePhoneNumberFromString from 'libphonenumber-js';
import { isEmpty, validateAmount, validateEmail } from 'utils/validator';
import MainCard from 'ui-component/cards/MainCard';
import { get, post, put } from '../../axios/apiService';
import LoadingBackdrop from 'ui-component/full-screen-loadder/LoadingBackdrop';
import { Controller, useForm } from 'react-hook-form';
import { useMemo } from 'react';
import { styled } from '@mui/system';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { InputAdornment, IconButton, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';


const CustomPopper = styled(Popper)({
    '& .MuiAutocomplete-paper': {
        backgroundColor: '#f5f5f5',
        color: '#000'
    }
});

function AddEditCouponForm({ title = '' }) {
    const theme = useTheme();
    const id = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const [discountTypes, setDiscountTypes] = useState([]);
    const [purchaseTypes, setPurchaseTypes] = useState([]);
    const [users, setUsers] = useState([]);
    const [targetType, setTargetType] = useState('all');
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingData, setPendingData] = useState(null);

    const { control, handleSubmit, reset, getValues, watch } = useForm({
        defaultValues: {
            name: '',
            code: '',
            categoryId: '',
            discountTypeId: '',
            discountValue: '',
            minPurchaseAmount: '',
            maxDiscountAmount: '',
            validFrom: dayjs(),
            validTo: null,
            redemptionCount: 1,
            isSingleUsePerUser: false,
            purchaseTypeId: '',
            targetAudience: 0, 
            isGeoLocationEnabled: false,
            geoLocations: [],
            notifyCoupon: false,
            isUnlimitedRedemptionCount: false, 
            maxUsePerUser: 1,
            description: '',  
        }
    });

    const [formErrors, setFormErrors] = useState({});
    const [isValidation, setIsValidation] = useState(false);

    // Fetch dropdown data
    const fetchDropdowns = async () => {
        try {
            let catRes = await get('WillTypeMaster/Category');
            let disRes = await get('WillTypeMaster/DiscountType');
            let purRes = await get('WillTypeMaster/PurchaseType');
            let userRes = await get('Customer?page=0&pageSize=10000');

            setCategories(catRes || []);
            setDiscountTypes(disRes || []);
            setPurchaseTypes(purRes || []);
            setUsers(userRes?.values?.map(u => ({ id: u.id, name: u.emailId })) || []);

        } catch (err) {
            console.error(err.message);
        }
    };

   
    useEffect(() => { fetchDropdowns(); }, []);

    // Load coupon for edit
    useEffect(() => {
        if (id.id) {
            const getById = async () => {
                setIsLoading(true);
                try {
                    let response = await get(`Coupon/${id.id}`, null);
                    if (response) {
                        reset({
                            name: response.name || '',
                            code: response.code || '',
                            categoryId: response.categoryId,
                            discountTypeId: response.discountTypeId,
                            discountValue: response.discountValue,
                            minPurchaseAmount: response.minPurchaseAmount,
                            maxDiscountAmount: response.maxDiscountAmount,
                            validFrom: dayjs(response.validFrom),
                            validTo: response.validTo ? dayjs(response.validTo) : null,
                            redemptionCount: response.redemptionCount,
                            isSingleUsePerUser: response.isSingleUsePerUser,
                            purchaseTypeId: response.purchaseTypeId,
                            assignedUserIds: response.assignedUserIds || [],
                            targetAudience: response.targetAudience,
                            isGeoLocationEnabled: response.isGeoLocationEnabled,
                            geoLocations: response.geoLocations ? response.geoLocations.split(',') : [],
                            maxUsePerUser: response.maxUsePerUser,
                            description: response.description || '',
                            isUnlimitedRedemptionCount: response.isUnlimitedRedemptionCount || false,
                        });
                        setTargetType(response.targetAudience === 0 ? 'all' : 'specific');

                    }
                } catch (err) {
                    console.error(err.message);
                    toast.error(err.response?.data || "Error loading coupon");
                } finally {
                    setIsLoading(false);
                }
            };
            getById();
        }
    }, [id]);

    const validator = () => {
        let data = getValues();
        const errors = {};
        if (isEmpty(data?.name)) errors.name = "Please enter Coupon Name";
        if (isEmpty(data?.code)) errors.code = "Please enter Coupon Code";
        if (!validateAmount(data?.categoryId, 1)) errors.categoryId = "Please select Category";
        if (!validateAmount(data?.discountTypeId, 1)) errors.discountTypeId = "Please select Discount Type";
        if (data?.discountValue === null || data?.discountValue === undefined || data?.discountValue === '') {
            errors.discountValue = "Please enter Discount Value";
        }
        if (data?.minPurchaseAmount === null || data?.minPurchaseAmount === undefined || data?.minPurchaseAmount === '') {
            errors.minPurchaseAmount = "Please enter Min Purchase Amount";
        }
        if (!validateAmount(data?.purchaseTypeId, 1)) errors.purchaseTypeId = "Please select Purchase Type";

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

const onSubmit = async (data) => {
    setIsValidation(true);
    if (!validator()) return;


    if (data.notifyCoupon) {
        setPendingData(data);     
        setConfirmOpen(true); 
        return;                  
    }

    saveCoupon(data);
    
};

const saveCoupon = async (data) => {
    const isSpecific = data.targetAudience === 1; // <-- FIXED

    const payload = {
        ...data,
        geoLocations: (data.geoLocations || []).join(','), 
        assignedUserIds: isSpecific ? (data.assignedUserIds || []) : [],
        targetAudience: isSpecific ? 1 : 0  // ensure backend gets single int
    };

    const apiUrl = id.id ? `coupon/${id.id}` : 'coupon';
    try {
        setIsLoading(true);
        let response = id.id ? await put(apiUrl, payload) : await post(apiUrl, payload);
        toast.success(id.id ? 'Coupon updated successfully' : 'Coupon added successfully');
        navigate('/coupon-management');

    } catch (error) {
        toast.error(error?.response?.data || "Error saving coupon");
    } finally {
        setIsLoading(false);
    }
};


const stateList = [
  { code: "AN", name: "Andaman and Nicobar Islands" },
  { code: "AP", name: "Andhra Pradesh" },
  { code: "AR", name: "Arunachal Pradesh" },
  { code: "AS", name: "Assam" },
  { code: "BR", name: "Bihar" },
  { code: "CG", name: "Chandigarh" },
  { code: "CH", name: "Chhattisgarh" },
  { code: "DH", name: "Dadra and Nagar Haveli" },
  { code: "DD", name: "Daman and Diu" },
  { code: "DL", name: "Delhi" },
  { code: "GA", name: "Goa" },
  { code: "GJ", name: "Gujarat" },
  { code: "HR", name: "Haryana" },
  { code: "HP", name: "Himachal Pradesh" },
  { code: "JK", name: "Jammu and Kashmir" },
  { code: "JH", name: "Jharkhand" },
  { code: "KA", name: "Karnataka" },
  { code: "KL", name: "Kerala" },
  { code: "LD", name: "Lakshadweep" },
  { code: "MP", name: "Madhya Pradesh" },
  { code: "MH", name: "Maharashtra" },
  { code: "MN", name: "Manipur" },
  { code: "ML", name: "Meghalaya" },
  { code: "MZ", name: "Mizoram" },
  { code: "NL", name: "Nagaland" },
  { code: "OR", name: "Odisha" },
  { code: "PY", name: "Puducherry" },
  { code: "PB", name: "Punjab" },
  { code: "RJ", name: "Rajasthan" },
  { code: "SK", name: "Sikkim" },
  { code: "TN", name: "Tamil Nadu" },
  { code: "TS", name: "Telangana" },
  { code: "TR", name: "Tripura" },
  { code: "UK", name: "Uttarakhand" },
  { code: "UP", name: "Uttar Pradesh" },
  { code: "WB", name: "West Bengal" }
];


return (
  <div>
    <MainCard title={title} backButton={{ onClick: () => navigate('/coupon-management') }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>

          {/* Coupon Details Section */}
          <Grid item xs={12}>
            <fieldset style={{
              border: '2px solid #364152',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '20px',
            }}>
              <legend style={{
                color: '#364152',
                fontSize: '1.2em',
                padding: '0 10px',
                fontWeight: 'bold',
                backgroundColor: '#f0f0f0',
                borderRadius: '5px',
              }}>
                Coupon Details
              </legend>
              <Grid container spacing={2}>

                {/* Coupon Name */}
                <Grid item xs={12} md={6} lg={4}>
                  <Controller
                    name="name"
                    control={control}
                    rules={{ required: 'Coupon Name is required' }}
                    render={({ field, fieldState }) =>
                      <TextField {...field} fullWidth label="Coupon Name *" error={!!fieldState.error} helperText={fieldState.error?.message} />
                    }
                  />
                </Grid>

                {/* Coupon Code (Auto-generated, disabled) */}
                <Grid item xs={12} md={6} lg={4}>
                  <Controller
                    name="code"
                    control={control}
                    rules={{ required: 'Coupon Code is required' }}
                    render={({ field, fieldState }) =>
                      <TextField {...field} fullWidth label="Coupon Code *" error={!!fieldState.error} helperText={fieldState.error?.message} />
                    }
                  />
                </Grid>

                {/* Target Audience */}
                <Grid item xs={12} md={6} lg={4}>
                  <Controller
                    name="targetAudience"
                    control={control}
                    rules={{ required: 'Target Audience is required' }}
                    render={({ field, fieldState }) => (
                      <Autocomplete
                        options={[
                          { id: 0, name: 'All' },
                          { id: 1, name: 'Specific User' }
                        ]}
                        getOptionLabel={(option) => option.name}
                        onChange={(e, newValue) => {
                          setTargetType(newValue?.id === 1 ? 'specific' : 'all');
                          field.onChange(newValue?.id || 0);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Target Audience *"
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                          />
                        )}
                        value={[
                          { id: 0, name: 'All' },
                          { id: 1, name: 'Specific User' }
                        ].find(opt => opt.id === field.value) || null}
                      />
                    )}
                  />
                </Grid>


                {/* Show Specific User dropdown only if targetType == 'specific' */}
                {targetType === 'specific' && (
                  <Grid item xs={12} md={6} lg={4}>
                    <Controller
                      name="assignedUserIds"
                      control={control}
                      rules={{ required: 'Please select at least one user' }}
                      render={({ field, fieldState }) => (
                        <Autocomplete
                          multiple
                          options={users}
                          getOptionLabel={(option) => option.name}
                          onChange={(e, newValue) => field.onChange(newValue.map(u => u.id))}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Select Users *"
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message}
                            />
                          )}
                          value={users.filter(u => field.value?.includes(u.id))}
                        />
                      )}
                    />
                  </Grid>
                )}

                {/* Category */}
                <Grid item xs={12} md={6} lg={4}>
                  <Controller
                    name="categoryId"
                    control={control}
                    rules={{ required: 'Category is required' }}
                    render={({ field, fieldState }) => (
                      <Autocomplete
                        options={categories}
                        getOptionLabel={(option) => option.name}
                        onChange={(e, newValue) => field.onChange(newValue?.id || '')}
                        renderInput={(params) => (
                          <TextField {...params} label="Category *" error={!!fieldState.error} helperText={fieldState.error?.message} />
                        )}
                        value={categories.find(opt => opt.id === field.value) || null}
                      />
                    )}
                  />
                </Grid>

                {/* Discount Type */}
                <Grid item xs={12} md={6} lg={4}>
                  <Controller
                    name="discountTypeId"
                    control={control}
                    rules={{ required: 'Discount Type is required' }}
                    render={({ field, fieldState }) => (
                      <Autocomplete
                        options={discountTypes}
                        getOptionLabel={(option) => option.name}
                        onChange={(e, newValue) => field.onChange(newValue?.id || '')}
                        renderInput={(params) => (
                          <TextField {...params} label="Discount Type *" error={!!fieldState.error} helperText={fieldState.error?.message} />
                        )}
                        value={discountTypes.find(opt => opt.id === field.value) || null}
                      />
                    )}
                  />
                </Grid>

                {/* Discount Value */}
              <Grid item xs={12} md={6} lg={4}>
                <Controller
                  name="discountValue"
                  control={control}
                  rules={{ required: 'Discount Value is required' }}
                  render={({ field, fieldState }) => {
                    const selectedDiscountTypeId = watch('discountTypeId');
                    const selectedDiscountType = discountTypes.find(dt => dt.id === selectedDiscountTypeId);
                    const tooltipText = selectedDiscountType?.name?.toLowerCase() === 'percentage'
                      ? "Enter discount in percentage"
                      : selectedDiscountType?.name?.toLowerCase() === 'fixedamount'
                        ? "Enter discount as a fixed amount"
                        : "Enter discount value";

                    return (
                      <TextField
                        {...field}
                        type="number"
                        fullWidth
                        label="Discount Value *"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip title={tooltipText} placement="top">
                                <IconButton size="small">
                                  <InfoOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          )
                        }}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    );
                  }}
                />
              </Grid>


                {/* Min Purchase */}
                <Grid item xs={12} md={6} lg={4}>
                  <Controller
                    name="minPurchaseAmount"
                    control={control}
                    rules={{ required: 'Min Purchase Amount is required' }}
                    render={({ field, fieldState }) =>
                      <TextField {...field} type="number" fullWidth label="Min Purchase Amount *" error={!!fieldState.error} helperText={fieldState.error?.message} />
                    }
                  />
                </Grid>

                {/* Max Discount */}
                <Grid item xs={12} md={6} lg={4}>
                  <Controller
                    name="maxDiscountAmount"
                    control={control}
                    render={({ field }) =>
                      <TextField {...field} type="number" fullWidth label="Max Discount Amount" />
                    }
                  />
                </Grid>

                {/* Purchase Type */}
                <Grid item xs={12} md={6} lg={4}>
                  <Controller
                    name="purchaseTypeId"
                    control={control}
                    rules={{ required: 'Purchase Type is required' }}
                    render={({ field, fieldState }) => (
                      <Autocomplete
                        options={purchaseTypes}
                        getOptionLabel={(option) => option.name}
                        onChange={(e, newValue) => field.onChange(newValue?.id || '')}
                        renderInput={(params) => (
                          <TextField {...params} label="Purchase Type *" error={!!fieldState.error} helperText={fieldState.error?.message} />
                        )}
                        value={purchaseTypes.find(opt => opt.id === field.value) || null}
                      />
                    )}
                  />
                </Grid>

              {/* isGeoLocationEnabled */}
              <Grid item xs={12} md={6} lg={4}>
                  <Controller
                      name="isGeoLocationEnabled"
                      control={control}
                      render={({ field }) => (
                          <FormControlLabel
                              control={ 
                                  <Switch
                                      checked={field.value}
                                      onChange={(e) => field.onChange(e.target.checked)}
                                  />
                              }
                              label="Enable Geo Location"
                          />
                      )}
                  />
              </Grid>

                {/* Geolocation */}
              <Grid item xs={12} md={6} lg={4}>
              <Controller
                  name="geoLocations"
                  control={control}
                  render={({ field }) => (
                      <Autocomplete
                          multiple
                          options={stateList}
                          getOptionLabel={(option) => option.name}
                          onChange={(e, newValue) => field.onChange(newValue.map(item => item.code))}
                          renderInput={(params) => (
                              <TextField
                                  {...params}
                                  label="Geo Locations"
                                  disabled={!watch("isGeoLocationEnabled")} // <-- Disable when false
                              />
                          )}
                          value={stateList.filter(s => field.value.includes(s.code))}
                          disabled={!watch("isGeoLocationEnabled")} // <-- Also disable Autocomplete
                      />
                  )}
              />
            </Grid>

          {/* Unlimited Redemption Count (Switch) */}
          <Grid item xs={12} md={6} lg={4}>
            <Controller
              name="isUnlimitedRedemptionCount"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label="Unlimited Redemption Count"
                />
              )}
            />
          </Grid>

            {/* Redemption Count */}
            <Grid item xs={12} md={6} lg={4}>
              <Controller
                name="redemptionCount"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    fullWidth
                    label="Redemption Count"
                    disabled={watch('isUnlimitedRedemptionCount')} // Disable when checkbox is false
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title="Max number of times the coupon can be used." placement="top">
                            <IconButton size="small">
                              <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      )
                    }}
                  />
                )}
              />
            </Grid>

            {/* Max Use Per User */}
            <Grid item xs={12} md={6} lg={4}>
              <Controller
                name="maxUsePerUser"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    fullWidth
                    label="Max Use Per User"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title="Maximum times a single user can use this coupon." placement="top">
                            <IconButton size="small">
                              <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      )
                    }}
                  />
                )}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12} md={12}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={3}
                    label="Description"
                    placeholder="Enter coupon description"
                  />
                )}
              />
            </Grid>

              </Grid>
            </fieldset>
          </Grid>

          {/* Validity Section */}
          <Grid item xs={12}>
            <fieldset style={{
              border: '2px solid #364152',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '20px',
            }}>
              <legend style={{
                color: '#364152',
                fontSize: '1.2em',
                padding: '0 10px',
                fontWeight: 'bold',
                backgroundColor: '#f0f0f0',
                borderRadius: '5px',
              }}>
                Validity
              </legend>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6} lg={4}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Controller name="validFrom" control={control} render={({ field }) =>
                      <DatePicker label="Valid From" value={field.value} onChange={field.onChange} slotProps={{ textField: { fullWidth: true } }} />
                    } />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Controller name="validTo" control={control} render={({ field }) =>
                      <DatePicker label="Valid To" value={field.value} onChange={field.onChange} slotProps={{ textField: { fullWidth: true } }} />
                    } />
                  </LocalizationProvider>
                </Grid>
              </Grid>
            </fieldset>
          </Grid>

           {/* Notify Coupon Checkbox (New) */}
           {!id.id && (
            <Grid item xs={12}>
              <Controller
                name="notifyCoupon"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value || false}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Notify Coupon"
                  />
                )}
              />
            </Grid>
          )}

        </Grid>

        <Button type="submit" variant="contained" sx={{ marginTop: '20px', padding: '5px 30px' }}>
          Save
        </Button>
      </form>
    </MainCard>

    <LoadingBackdrop open={isLoading} />

    {/* Confirmation Dialog */}
    <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
      <DialogTitle>Confirm Notification</DialogTitle>
      <DialogContent>
        Are you sure you want to notify users about this coupon?
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={() => { 
            setConfirmOpen(false);
            setPendingData(null);
            reset({ ...getValues(), notifyCoupon: false }); // uncheck
          }} 
          color="secondary"
        >
          Cancel
        </Button>
        <Button 
          onClick={() => { 
            setConfirmOpen(false);
            if (pendingData) saveCoupon(pendingData); // proceed to save automatically
          }} 
          color="primary" 
          variant="contained"
        >
                Yes, Notify
        </Button>
      </DialogActions>
    </Dialog>



  </div>
);




}

export default AddEditCouponForm;
